import React, { useState, useEffect } from 'react';
import { getMarketplaces, startKeepaPull, runUnmatched } from '../api';

interface Marketplace {
  id: number;
  code: string;
  domain: string;
}

interface KeepaPullModalProps {
  /** Specific product IDs to pull; undefined = all products */
  productIds?: string[];
  /** Pre-check these marketplace codes (e.g. codes that already have mappings) */
  preselectedCodes?: string[];
  /** Label shown in the modal header for context */
  contextLabel?: string;
  onClose: () => void;
  /** Called after jobs are queued. Passes job IDs (one per marketplace). */
  onStarted: (jobIds: string[]) => void;
}

const KeepaPullModal: React.FC<KeepaPullModalProps> = ({
  productIds,
  preselectedCodes,
  contextLabel,
  onClose,
  onStarted,
}) => {
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getMarketplaces()
      .then(res => {
        const mps: Marketplace[] = res.data;
        setMarketplaces(mps);
        // Pre-select: use preselectedCodes if provided, otherwise select all
        const initial = preselectedCodes && preselectedCodes.length > 0
          ? new Set<string>(preselectedCodes)
          : new Set<string>(mps.map(m => m.code));
        setSelected(initial);
      })
      .catch(() => setError('Failed to load marketplaces'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (code: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(marketplaces.map(m => m.code)));
  const deselectAll = () => setSelected(new Set());

  const run = async (type: 'pull' | 'title') => {
    if (selected.size === 0) {
      setError('Select at least one marketplace');
      return;
    }
    setSubmitting(true);
    setError('');
    const codes = marketplaces.filter(m => selected.has(m.code)).map(m => m.code);
    const jobIds: string[] = [];
    let i = 0;

    for (const code of codes) {
      i++;
      setProgress(`${type === 'pull' ? 'Pulling' : 'Searching'} ${code}… (${i}/${codes.length})`);
      try {
        const res = type === 'pull'
          ? await startKeepaPull(code, undefined, productIds)
          : await runUnmatched(code, productIds);
        if (res.data?.job_id) jobIds.push(res.data.job_id);
      } catch {
        // skip failed marketplace, continue with rest
      }
    }

    setSubmitting(false);
    onClose();
    onStarted(jobIds);
  };

  const productCount = productIds ? productIds.length : null;
  const countLabel = productCount
    ? `${productCount} product${productCount !== 1 ? 's' : ''}`
    : 'all products';

  return (
    <div className="modal-overlay" onClick={() => !submitting && onClose()}>
      <div
        className="card"
        style={{ maxWidth: 480, width: '100%' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="card-header">
          <div>
            <span className="card-title">Pull Keepa Data</span>
            {contextLabel && (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                {contextLabel}
              </div>
            )}
          </div>
          <button className="modal-close" onClick={() => !submitting && onClose()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '0 0 4px' }}>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '14px' }}>
            Select the marketplaces to pull data for <strong>{countLabel}</strong>.
            One job will be queued per marketplace.
          </p>

          {error && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{error}</div>}

          {loading ? (
            <div className="loading" style={{ padding: '20px 0' }}>
              <div className="spinner" style={{ width: 18, height: 18 }} />
            </div>
          ) : (
            <>
              {/* Select all / none */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={selectAll}
                  disabled={submitting}
                >
                  Select All
                </button>
                <button
                  className="btn btn-secondary btn-small"
                  onClick={deselectAll}
                  disabled={submitting}
                >
                  Deselect All
                </button>
              </div>

              {/* Marketplace checkboxes */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
                {marketplaces.map(mp => (
                  <label
                    key={mp.code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '9px 12px',
                      border: '1px solid',
                      borderColor: selected.has(mp.code) ? 'var(--accent)' : 'var(--border-primary)',
                      borderRadius: 'var(--radius-md)',
                      background: selected.has(mp.code) ? 'var(--accent-soft)' : 'var(--bg-card-hover)',
                      cursor: submitting ? 'default' : 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(mp.code)}
                      onChange={() => !submitting && toggle(mp.code)}
                      style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }}
                    />
                    <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', minWidth: 28 }}>
                      {mp.code}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{mp.domain}</span>
                    {preselectedCodes?.includes(mp.code) && (
                      <span style={{
                        marginLeft: 'auto', fontSize: '10px', fontWeight: 600,
                        color: 'var(--success)', background: 'rgba(63,185,80,0.1)',
                        border: '1px solid rgba(63,185,80,0.25)',
                        padding: '1px 6px', borderRadius: '10px',
                      }}>
                        mapped
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Progress */}
          {submitting && progress && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              fontSize: '13px', color: 'var(--text-muted)',
              marginBottom: '14px',
            }}>
              <svg className="column-toggle-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              {progress}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => !submitting && onClose()}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => run('title')}
              disabled={submitting || loading || selected.size === 0}
              title="Search Keepa by product title to find ASIN candidates"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              Title Search
            </button>
            <button
              className="btn btn-primary"
              onClick={() => run('pull')}
              disabled={submitting || loading || selected.size === 0}
              title="Pull product data (price, fees, image) from Keepa using barcodes/ASINs"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
              {submitting ? progress || 'Starting…' : `Pull Data${selected.size > 0 ? ` (${selected.size})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeepaPullModal;
